import {
  CHATTEL_FIELD_NAME,
  CHATTEL_HOME_CONDITION_LABEL,
  CHATTEL_HOME_TYPE_LABEL,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/ChattelDetailsSection/ChattelDetailsSection.constants';
import { formatChattelSectionNumbers } from 'app/pages/loanApplication/tabs/propertyAndLoan/ChattelDetailsSection/ChattelDetailsSection.utils';
import { getChattelDownPaymentFromContract } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.utils';
import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { BorrowerType } from 'constants/loanApplication';
import { getConditionalArray } from 'utils/data';
import { formatCalendarDate } from 'utils/date';
import { formatCurrencyValue } from 'utils/format';
import { getAddressData, getBorrowerFullName } from 'utils/loanApplication';
import { formatNumberToFixed3Decimals } from 'utils/number';
import { formatPhoneNumberForDisplay } from 'utils/phone';

import {
  WORKSHEET_LOAN_FACTS_LABEL,
  WORKSHEET_LOAN_FACTS_MAILING_SAME_AS_CURRENT,
  WORKSHEET_LOAN_FACTS_SECTION_TITLE,
  WORKSHEET_LOAN_FACTS_SSN_VISIBLE_DIGITS,
} from './WorksheetLoanFactsPanel.constants';

const toDisplayValue = value => (_.isNil(value) || value === '' ? null : String(value));

// Only the last four digits: the audit confirms identity, it never needs the full number on screen.
export const maskSsn = ssn => {
  const digits = String(ssn ?? '').replace(/\D/g, '');

  return digits.length < WORKSHEET_LOAN_FACTS_SSN_VISIBLE_DIGITS
    ? null
    : `***-**-${digits.slice(-WORKSHEET_LOAN_FACTS_SSN_VISIBLE_DIGITS)}`;
};

export const formatLoanFactsAddress = address => {
  if (!address) {
    return null;
  }

  const { street, unit, city, state, zip } = getAddressData(address);
  const stateZip = [state, zip].filter(Boolean).join(' ');

  return (
    [[street, unit].filter(Boolean).join(' '), city, stateZip].filter(Boolean).join(', ') || null
  );
};

// Shown like the loan editor's Loan & Lock Info card (8.250%).
const formatInterestRate = rate =>
  _.isNil(rate) ? null : `${formatNumberToFixed3Decimals(rate)}%`;

// A borrower carries a mailing address only when it differs from the current residence.
const formatMailingAddress = mailingAddress =>
  mailingAddress
    ? formatLoanFactsAddress(mailingAddress.address)
    : WORKSHEET_LOAN_FACTS_MAILING_SAME_AS_CURRENT;

const formatTermMonths = months => (_.isNil(months) ? null : `${months} months`);

const formatHomeSize = ({ width1, length1 }) =>
  _.isNil(width1) || _.isNil(length1) ? null : `${width1} × ${length1}`;

const buildBorrowerSections = ({ borrowers, borrowersView }) => {
  const currentAddressById = _.keyBy(borrowersView ?? [], 'id');
  const coBorrowerCount = (borrowers ?? []).filter(
    ({ borrowerType }) => borrowerType !== BorrowerType.PRIMARY,
  ).length;

  return _.sortBy(borrowers ?? [], 'order').map((borrower, index) => {
    const isPrimary = borrower.borrowerType === BorrowerType.PRIMARY;
    const coBorrowerNumber = coBorrowerCount > 1 ? ` ${index}` : '';

    return {
      id: borrower.id,
      title: isPrimary
        ? WORKSHEET_LOAN_FACTS_SECTION_TITLE.BORROWER
        : `${WORKSHEET_LOAN_FACTS_SECTION_TITLE.CO_BORROWER}${coBorrowerNumber}`,
      rows: [
        { label: WORKSHEET_LOAN_FACTS_LABEL.NAME, value: getBorrowerFullName(borrower) || null },
        {
          label: WORKSHEET_LOAN_FACTS_LABEL.DATE_OF_BIRTH,
          value: borrower.dateOfBirth
            ? formatCalendarDate(borrower.dateOfBirth, COMMON_DATE_FORMAT)
            : null,
        },
        { label: WORKSHEET_LOAN_FACTS_LABEL.SSN, value: maskSsn(borrower.ssn) },
        {
          label: WORKSHEET_LOAN_FACTS_LABEL.HOME_PHONE,
          value: formatPhoneNumberForDisplay(borrower.homePhone) || null,
        },
        {
          label: WORKSHEET_LOAN_FACTS_LABEL.CELL_PHONE,
          value: formatPhoneNumberForDisplay(borrower.cellPhone) || null,
        },
        { label: WORKSHEET_LOAN_FACTS_LABEL.EMAIL, value: toDisplayValue(borrower.email) },
        {
          label: WORKSHEET_LOAN_FACTS_LABEL.CURRENT_ADDRESS,
          value: toDisplayValue(currentAddressById[borrower.id]?.address),
        },
        {
          label: WORKSHEET_LOAN_FACTS_LABEL.MAILING_ADDRESS,
          value: formatMailingAddress(borrower.mailingAddress),
        },
      ],
    };
  });
};

// As the loan editor reads it: off the chattel sales contract, else the stored down payment.
export const getWorksheetDownPayment = ({ loanInfo, chattelDetails }) => {
  const details = loanInfo?.loan?.details;

  return (
    getChattelDownPaymentFromContract({
      chattelDetails,
      baseLoanAmount: details?.baseLoanAmount,
    }) ??
    details?.downPaymentValue ??
    null
  );
};

export const getProposedPropertyExpenses = subjectProperty =>
  subjectProperty?.propertyExpenses?.proposedPropertyExpenses;

const buildLoanSection = ({ loanInfo, proposedPropertyExpenses, chattelDetails }) => {
  const { program, details } = loanInfo?.loan ?? {};

  return {
    id: 'loan',
    title: WORKSHEET_LOAN_FACTS_SECTION_TITLE.LOAN,
    rows: [
      {
        label: WORKSHEET_LOAN_FACTS_LABEL.LOAN_AMOUNT,
        value: formatCurrencyValue(details?.totalLoanAmount),
      },
      { label: WORKSHEET_LOAN_FACTS_LABEL.INTEREST_RATE, value: formatInterestRate(program?.rate) },
      { label: WORKSHEET_LOAN_FACTS_LABEL.TERM, value: formatTermMonths(program?.loanPeriod) },
      {
        label: WORKSHEET_LOAN_FACTS_LABEL.MONTHLY_PAYMENT,
        value: formatCurrencyValue(proposedPropertyExpenses?.principalAndInterest),
      },
      {
        label: WORKSHEET_LOAN_FACTS_LABEL.DOWN_PAYMENT,
        value: formatCurrencyValue(getWorksheetDownPayment({ loanInfo, chattelDetails })),
      },
    ],
  };
};

const buildHomeSection = chattelDetails => ({
  id: 'home',
  title: WORKSHEET_LOAN_FACTS_SECTION_TITLE.HOME,
  rows: [
    { label: CHATTEL_FIELD_NAME.YEAR_BUILT, value: toDisplayValue(chattelDetails.yearBuilt) },
    { label: CHATTEL_FIELD_NAME.MAKE, value: toDisplayValue(chattelDetails.make) },
    { label: CHATTEL_FIELD_NAME.MODEL, value: toDisplayValue(chattelDetails.model) },
    {
      label: CHATTEL_FIELD_NAME.SERIAL_NUMBER,
      value: formatChattelSectionNumbers([
        chattelDetails.serialNumber,
        chattelDetails.serialNumber2,
        chattelDetails.serialNumber3,
      ]),
    },
    {
      label: CHATTEL_FIELD_NAME.HUD_LABEL,
      value: formatChattelSectionNumbers([
        chattelDetails.hudLabelNumber,
        chattelDetails.hudLabelNumber2,
        chattelDetails.hudLabelNumber3,
      ]),
    },
    {
      label: CHATTEL_FIELD_NAME.HOME_TYPE,
      value: CHATTEL_HOME_TYPE_LABEL[chattelDetails.homeType] ?? null,
    },
    {
      label: CHATTEL_FIELD_NAME.CONDITION,
      value: CHATTEL_HOME_CONDITION_LABEL[chattelDetails.condition] ?? null,
    },
    { label: WORKSHEET_LOAN_FACTS_LABEL.HOME_SIZE, value: formatHomeSize(chattelDetails) },
  ],
});

const buildLocationSection = ({ propertyAddress, chattelDetails }) => ({
  id: 'location',
  title: WORKSHEET_LOAN_FACTS_SECTION_TITLE.LOCATION,
  rows: [
    {
      label: WORKSHEET_LOAN_FACTS_LABEL.PROPERTY_ADDRESS,
      value: formatLoanFactsAddress(propertyAddress),
    },
    ...getConditionalArray(!!chattelDetails, () => [
      {
        label: CHATTEL_FIELD_NAME.LOCATION_DESCRIPTION,
        value: toDisplayValue(chattelDetails.locationDescription),
      },
      { label: CHATTEL_FIELD_NAME.PARK_NAME, value: toDisplayValue(chattelDetails.parkName) },
      { label: CHATTEL_FIELD_NAME.LOT_NUMBER, value: toDisplayValue(chattelDetails.lotNumber) },
      {
        label: CHATTEL_FIELD_NAME.MONTHLY_LOT_RENT,
        value: formatCurrencyValue(chattelDetails.monthlyLotRent),
      },
      {
        label: CHATTEL_FIELD_NAME.LANDOWNER_NAME,
        value: toDisplayValue(chattelDetails.landownerName),
      },
      {
        label: CHATTEL_FIELD_NAME.LANDOWNER_PHONE,
        value: formatPhoneNumberForDisplay(chattelDetails.landownerPhone) || null,
      },
      {
        label: CHATTEL_FIELD_NAME.LAND_MONTHLY_PAYMENT,
        value: formatCurrencyValue(chattelDetails.landMonthlyPayment),
      },
    ]),
  ],
});

const buildInsuranceSection = proposedPropertyExpenses => ({
  id: 'insurance',
  title: WORKSHEET_LOAN_FACTS_SECTION_TITLE.INSURANCE,
  rows: [
    {
      label: WORKSHEET_LOAN_FACTS_LABEL.MONTHLY_HOMEOWNERS_INSURANCE,
      value: formatCurrencyValue(proposedPropertyExpenses?.homeownersInsurance),
    },
  ],
});

/**
 * Shapes the loan responses read by getWorksheetLoanFactsAction into the read-only sections the
 * worksheet drawer shows (LEN-132 Pre-Funding Phone Audit). The home section needs chattel details;
 * a loan without them gets the borrower, loan, location and insurance sections only.
 */
export const buildWorksheetLoanFacts = ({
  borrowers,
  borrowersView,
  loanInfo,
  subjectProperty,
  chattelDetails = null,
}) => {
  const proposedPropertyExpenses = getProposedPropertyExpenses(subjectProperty);
  const propertyAddress = subjectProperty?.property?.propertyAddress?.address?.address ?? null;

  return [
    ...buildBorrowerSections({ borrowers, borrowersView }),
    buildLoanSection({ loanInfo, proposedPropertyExpenses, chattelDetails }),
    ...getConditionalArray(!!chattelDetails, () => [buildHomeSection(chattelDetails)]),
    buildLocationSection({ propertyAddress, chattelDetails }),
    buildInsuranceSection(proposedPropertyExpenses),
  ];
};
